import { forwardRef, useId, type ReactNode } from "react";
import { Checkbox as Check, RadioGroup as Radio, Slider as Range, Switch as Toggle, ToggleGroup } from "radix-ui";
import { Icon } from "../Icon";
import type { SelectItem } from "./fields";

export interface CheckboxProps {
  checked: boolean | "indeterminate";
  onCheckedChange?: (checked: boolean | "indeterminate") => void;
  label: ReactNode;
  disabled?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
  className?: string;
  "aria-describedby"?: string;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox({ checked, onCheckedChange, label, disabled, id: givenId, name, required, className = "", ...aria }, ref) {
  const autoId = useId(), id = givenId ?? autoId;
  return <label className={`pa-choice ${className}`} htmlFor={id}><Check.Root {...aria} ref={ref} id={id} className="pa-checkbox" checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} name={name} required={required}><Check.Indicator>{checked === "indeterminate" ? <span aria-hidden="true">−</span> : <Icon name="yes" size={17} />}</Check.Indicator></Check.Root><span>{label}</span></label>;
});

export interface RadioGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  items: readonly (Omit<SelectItem, "label"> & { label: ReactNode; descriptionId?: string })[];
  label: string;
  disabled?: boolean;
  name?: string;
  required?: boolean;
  orientation?: "horizontal" | "vertical";
  className?: string;
}

export function RadioGroup({ value, onValueChange, items, label, disabled, name, required, orientation = "vertical", className = "" }: RadioGroupProps) {
  const id = useId();
  return <Radio.Root className={`pa-radio-group ${className}`} value={value} onValueChange={onValueChange} aria-label={label} disabled={disabled} name={name} required={required} orientation={orientation}>{items.map((item, index) => <label className="pa-choice pa-radio-choice" key={item.value} htmlFor={`${id}-${index}`}><Radio.Item id={`${id}-${index}`} className="pa-radio" value={item.value} disabled={item.disabled} aria-describedby={[item.descriptionId, item.description ? `${id}-${index}-description` : undefined].filter(Boolean).join(" ") || undefined}><Radio.Indicator className="pa-radio-dot" /></Radio.Item><span>{item.label}{item.description && <small id={`${id}-${index}-description`}>{item.description}</small>}</span></label>)}</Radio.Root>;
}

export interface SwitchProps {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
  className?: string;
  "aria-describedby"?: string;
}

export function Switch({ checked, onCheckedChange, label, disabled, id: givenId, name, required, className = "", ...aria }: SwitchProps) {
  const autoId = useId(), id = givenId ?? autoId;
  return <div className={`pa-switch-line ${className}`}><Toggle.Root {...aria} id={id} className="pa-switch" checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} name={name} required={required}><Toggle.Thumb className="pa-switch-thumb" /></Toggle.Root><label htmlFor={id}>{label}</label></div>;
}

export interface SliderProps {
  value: number;
  onValueChange: (value: number) => void;
  onValueCommit?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label: string;
  disabled?: boolean;
  name?: string;
  className?: string;
  "aria-valuetext"?: string;
}

export function Slider({ value, onValueChange, onValueCommit, min = 0, max = 100, step = 1, label, disabled, name, className = "", ...aria }: SliderProps) {
  return <Range.Root className={`pa-slider ${className}`} value={[value]} onValueChange={next => onValueChange(next[0])} onValueCommit={next => onValueCommit?.(next[0])} min={min} max={max} step={step} disabled={disabled} name={name}><Range.Track className="pa-slider-track"><Range.Range className="pa-slider-range" /></Range.Track><Range.Thumb {...aria} className="pa-slider-thumb" aria-label={label} aria-disabled={disabled || undefined} /></Range.Root>;
}

export function Segmented({ value, onValueChange, items, label, disabled, className = "" }: Omit<RadioGroupProps, "name" | "required" | "orientation">) {
  return <ToggleGroup.Root type="single" className={`pa-segment ${className}`} value={value} onValueChange={next => { if (next) onValueChange(next); }} aria-label={label} disabled={disabled}>{items.map(item => <ToggleGroup.Item key={item.value} value={item.value} disabled={item.disabled}>{item.label}</ToggleGroup.Item>)}</ToggleGroup.Root>;
}
